/**
 * Behavior Pattern Badge Component
 * Displays a color-coded badge for guest behavior patterns
 */

import React from 'react';
import { 
  CheckCircle, 
  Clock, 
  XCircle, 
  UserCheck, 
  AlertTriangle, 
  Minus, 
  TrendingUp,
  HelpCircle
} from 'lucide-react';

export type BehaviorPatternType = 
  | 'highly_responsive'
  | 'slow_responder'
  | 'non_responsive'
  | 'reliable_attender'
  | 'frequent_no_show'
  | 'consistent_decliner'
  | 'inconsistent'
  | 'insufficient_data';

interface BehaviorPatternBadgeProps {
  patternType: BehaviorPatternType;
  className?: string;
  showIcon?: boolean;
  showTooltip?: boolean;
}

const patternConfig = {
  highly_responsive: {
    label: 'Highly Responsive',
    icon: CheckCircle,
    color: 'bg-green-100 text-green-800 border-green-300',
    iconColor: 'text-green-600',
    description: 'Responds quickly and consistently to invitations. High engagement rate.'
  },
  slow_responder: {
    label: 'Slow Responder',
    icon: Clock,
    color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    iconColor: 'text-yellow-600',
    description: 'Takes longer to respond but generally engages when prompted.'
  },
  non_responsive: {
    label: 'Non-Responsive',
    icon: XCircle,
    color: 'bg-red-100 text-red-800 border-red-300',
    iconColor: 'text-red-600',
    description: 'Rarely or never responds to invitations.'
  },
  reliable_attender: {
    label: 'Reliable Attender',
    icon: UserCheck,
    color: 'bg-blue-100 text-blue-800 border-blue-300',
    iconColor: 'text-blue-600',
    description: 'Accepts invitations frequently and consistently attends events.'
  },
  frequent_no_show: {
    label: 'Frequent No-Show',
    icon: AlertTriangle,
    color: 'bg-orange-100 text-orange-800 border-orange-300',
    iconColor: 'text-orange-600',
    description: 'Accepts invitations but often doesn\'t show up to events.'
  },
  consistent_decliner: {
    label: 'Consistent Decliner',
    icon: Minus,
    color: 'bg-gray-100 text-gray-800 border-gray-300',
    iconColor: 'text-gray-600',
    description: 'Consistently declines or rejects invitations.'
  },
  inconsistent: {
    label: 'Inconsistent',
    icon: TrendingUp,
    color: 'bg-purple-100 text-purple-800 border-purple-300',
    iconColor: 'text-purple-600',
    description: 'Behavior varies significantly - no clear pattern.'
  },
  insufficient_data: {
    label: 'Insufficient Data',
    icon: HelpCircle,
    color: 'bg-gray-100 text-gray-600 border-gray-300',
    iconColor: 'text-gray-500',
    description: 'Not enough data to determine behavior pattern.'
  }
};

export const BehaviorPatternBadge: React.FC<BehaviorPatternBadgeProps> = ({ 
  patternType, 
  className = '',
  showIcon = true,
  showTooltip = false
}) => {
  const config = patternConfig[patternType] || patternConfig.insufficient_data;
  const Icon = config.icon;

  return (
    <div 
      className={`relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${config.color} ${className}`}
    >
      {showIcon && <Icon className={`w-4 h-4 ${config.iconColor}`} />}
      <span>{config.label}</span>
      
      {showTooltip && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
          {config.description}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

export default BehaviorPatternBadge;

